import jsPDF from 'jspdf'
import globeLogo from '@/assets/earth.png'
import netZeroLogo from '@/assets/netzero.png'
import shippingIcon from '@/assets/local_shipping_32dp_FFFFFF_FILL0_wght400_GRAD0_opsz40.png'
import gasIcon from '@/assets/local_gas_station_32dp_FFFFFF_FILL0_wght400_GRAD0_opsz40.png'
import co2Icon from '@/assets/energy_program_saving_32dp_FFFFFF_FILL0_wght400_GRAD0_opsz40.png'
import distanceIcon from '@/assets/distance_32dp_FFFFFF_FILL0_wght400_GRAD0_opsz40.png'
import efficiencyIcon from '@/assets/energy_program_time_used_32dp_FFFFFF_FILL0_wght400_GRAD0_opsz40.png'

// Neutral Fuels brand colors - Blue theme
const COLORS = {
  primary: '#1E40AF',        // Blue-800
  primaryDark: '#1E3A8A',   // Blue-900
  blue1: '#3B82F6',         // Blue-500
  blue2: '#2563EB',         // Blue-600
  blue3: '#1D4ED8',         // Blue-700
  blue4: '#1E40AF',         // Blue-800
  blue5: '#1E3A8A',         // Blue-900
  dark: '#1a1a1a',
  gray: '#666666',
  lightGray: '#999999',
  veryLightGray: '#f5f5f5',
  white: '#ffffff'
}

// Helper function to format numbers with K/M suffixes
const formatNumberWithSuffix = (num) => {
  if (typeof num !== 'number') return num
  if (num >= 1000000) return (num / 1000000).toFixed(2) + ' Mil'
  else if (num >= 1000) return (num / 1000).toFixed(2) + ' K'
  else return num.toFixed(2)
}

// Helper function to format numbers with commas (whole numbers)
const formatNumber = (num) => {
  if (typeof num !== 'number') return num
  return Math.round(num).toLocaleString()
}

// Helper function to parse number from formatted string
const parseFormattedNumber = (str) => {
  if (typeof str === 'number') return str
  // Remove 'K', 'M', 'Mil' suffixes and convert
  const multipliers = { K: 1000, M: 1000000, Mil: 1000000 }
  const match = str.match(/^([\d,.]+)\s*([KM]|Mil)?$/)
  if (match) {
    const num = parseFloat(match[1].replace(/,/g, ''))
    const suffix = match[2]
    return suffix ? num * multipliers[suffix] : num
  }
  return parseFloat(str.replace(/,/g, '')) || 0
}

// Helper to get icon for metric
const getIconForMetric = (title) => {
  if (title.includes('Assets') || title.includes('Vessels')) return shippingIcon
  if (title.includes('Distance')) return distanceIcon
  if (title.includes('Volume')) return gasIcon
  if (title.includes('CO2') || title.includes('Co2')) return co2Icon
  if (title.includes('Efficiency')) return efficiencyIcon
  if (title.includes('Deliveries')) return shippingIcon
  return null
}

export const generateSummaryPDF = async (data) => {
  const {
    reportTitle = 'Fleet Summary Report',
    dateRange,
    selectedAssets = 'All',
    customers = [],
    includeAllCustomers = false,
    currentCustomerIndex = 0
  } = data

  // Create PDF in landscape for better layout
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 15
  const contentWidth = pageWidth - (margin * 2)

  let yPosition = margin

  // Helper to draw rounded rectangle
  const roundedRect = (x, y, width, height, radius) => {
    // Using roundedRect method available in jsPDF
    doc.roundedRect(x, y, width, height, radius, radius, 'F')
  }

  // Helper to add new page if needed
  const checkPageBreak = (requiredSpace) => {
    if (yPosition + requiredSpace > pageHeight - margin) {
      doc.addPage()
      yPosition = margin
      return true
    }
    return false
  }

  // Draw header with logo and company name
  const drawHeader = () => {
    // Main header background (dark blue)
    doc.setFillColor(COLORS.primary)
    doc.rect(0, 0, pageWidth, 35, 'F')

    // Add globe logo on the left
    const logoSize = 20
    const logoX = margin
    const logoY = 7.5
    doc.addImage(globeLogo, 'PNG', logoX, logoY, logoSize, logoSize)

    // Company name next to logo
    doc.setTextColor(COLORS.white)
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('Neutral Fuels', logoX + logoSize + 5, 18)

    // Add NetZero logo on the right
    const netZeroSize = 20
    const netZeroX = pageWidth - margin - netZeroSize
    const netZeroY = 7.5
    doc.addImage(netZeroLogo, 'PNG', netZeroX, netZeroY, netZeroSize, netZeroSize)

    // Green accent bar
    doc.setFillColor('#9BC53D')  // Lime green from the design
    doc.rect(0, 35, pageWidth, 5, 'F')

    // Report title below header
    yPosition = 50
    doc.setTextColor(COLORS.dark)
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    doc.text(reportTitle, pageWidth / 2, yPosition, { align: 'center' })

    yPosition = 60
  }

  // Draw report info section
  const drawReportInfo = () => {
    doc.setFillColor(COLORS.veryLightGray)
    roundedRect(margin, yPosition, contentWidth, 25, 2)

    doc.setTextColor(COLORS.dark)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')

    const leftCol = margin + 5
    const rightCol = pageWidth / 2 + 5

    // Date Range
    doc.text('Date Range:', leftCol, yPosition + 8)
    doc.setFont('helvetica', 'normal')
    doc.text(dateRange, leftCol, yPosition + 15)

    // Selected Assets
    doc.setFont('helvetica', 'bold')
    doc.text('Selected Assets:', rightCol, yPosition + 8)
    doc.setFont('helvetica', 'normal')
    const assetsText = Array.isArray(selectedAssets)
      ? (selectedAssets.length > 0 ? selectedAssets.join(', ') : 'All')
      : selectedAssets
    doc.text(assetsText.length > 40 ? 'Multiple Assets Selected' : assetsText, rightCol, yPosition + 15)

    yPosition += 35
  }

  // Draw customer summary cards
  const drawCustomerSection = (customer, cards, isGroupCustomer = false) => {
    checkPageBreak(80)

    // Customer header - adjust height if site name exists
    const headerHeight = customer.site_name ? 16 : 12
    doc.setFillColor(COLORS.primary)
    roundedRect(margin, yPosition, contentWidth, headerHeight, 2)

    // Customer name
    doc.setTextColor(COLORS.white)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(customer.customer_name, margin + 5, yPosition + 8)

    // Site name underneath if exists
    if (customer.site_name) {
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text(customer.site_name, margin + 5, yPosition + 14)
    }

    yPosition += headerHeight + 5

    // Draw metric cards in a grid
    const cardWidth = (contentWidth - 10) / 3
    const cardHeight = 30
    const cardGap = 5

    cards.forEach((card, index) => {
      const row = Math.floor(index / 3)
      const col = index % 3
      const x = margin + (col * (cardWidth + cardGap))
      const y = yPosition + (row * (cardHeight + cardGap))

      // Card background - use blue shades
      const cardColor = card.color || COLORS.primary
      // Convert green shades to blue shades
      const blueShades = {
        '#96c33d': COLORS.blue1,
        '#8fb300': COLORS.blue2,
        '#7a9900': COLORS.blue3,
        '#698000': COLORS.blue4,
        '#586600': COLORS.blue5
      }
      const finalColor = blueShades[cardColor] || cardColor
      doc.setFillColor(finalColor)
      roundedRect(x, y, cardWidth, cardHeight, 2)

      // Icon
      const icon = getIconForMetric(card.title)
      if (icon) {
        const iconSize = 6
        try {
          const iconFormat = icon.endsWith('.png') ? 'PNG' : 'SVG'
          doc.addImage(icon, iconFormat, x + 4, y + 4, iconSize, iconSize)
        } catch (err) {
          console.log('Could not add icon:', err)
        }
      }

      // Card title
      doc.setTextColor(COLORS.white)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      const titleLines = card.title.split('\n')
      titleLines.forEach((line, lineIndex) => {
        doc.text(line, x + cardWidth / 2, y + 7 + (lineIndex * 4), { align: 'center' })
      })

      // Card value
      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text(card.calloutValue.toString(), x + cardWidth / 2, y + cardHeight - 7, { align: 'center' })
    })

    const maxRows = Math.ceil(cards.length / 3)
    yPosition += (maxRows * (cardHeight + cardGap)) + 10
  }

  // Draw summary totals for all customers
  const drawGrandTotals = (allCards, isGroupCustomer = false) => {
    checkPageBreak(50)

    doc.setFillColor(COLORS.primaryDark)
    doc.rect(margin, yPosition, contentWidth, 12, 'F')

    doc.setTextColor(COLORS.white)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Grand Total - All Customers', margin + 5, yPosition + 8)

    yPosition += 17

    // Calculate totals
    const totals = {
      assets: 0,
      volume: 0,
      co2: 0,
      distance: 0,
      efficiency: 0,
      deliveries: 0,
      efficiencyCount: 0
    }

    allCards.forEach(customerCards => {
      customerCards.forEach(card => {
        const value = parseFormattedNumber(card.calloutValue)

        if (card.title.includes('Assets') || card.title.includes('Vessels')) {
          totals.assets += value
        } else if (card.title.includes('Volume')) {
          totals.volume += value
        } else if (card.title.includes('CO2')) {
          totals.co2 += value
        } else if (card.title.includes('Distance')) {
          totals.distance += value
        } else if (card.title.includes('Efficiency')) {
          totals.efficiency += value
          totals.efficiencyCount++
        } else if (card.title.includes('Deliveries')) {
          totals.deliveries += value
        }
      })
    })

    const summaryCards = [
      {
        title: isGroupCustomer ? 'Total Vessels' : 'Total Assets',
        calloutValue: formatNumberWithSuffix(totals.assets),
        color: COLORS.blue1
      },
      {
        title: 'Total Volume\n(L)',
        calloutValue: formatNumberWithSuffix(totals.volume),
        color: COLORS.blue4
      },
      {
        title: 'Total CO2 Savings\n(Kg/Co2)',
        calloutValue: formatNumberWithSuffix(totals.co2),
        color: COLORS.blue5
      }
    ]

    if (!isGroupCustomer && totals.distance > 0) {
      summaryCards.splice(1, 0, {
        title: 'Total Distance\n(Km)',
        calloutValue: formatNumberWithSuffix(totals.distance),
        color: COLORS.blue2
      })

      if (totals.efficiencyCount > 0) {
        summaryCards.splice(2, 0, {
          title: 'Avg Efficiency\n(Km/L)',
          calloutValue: formatNumberWithSuffix(totals.efficiency / totals.efficiencyCount),
          color: COLORS.blue3
        })
      }
    } else if (isGroupCustomer && totals.deliveries > 0) {
      summaryCards.splice(1, 0, {
        title: 'Total Deliveries',
        calloutValue: formatNumberWithSuffix(totals.deliveries),
        color: COLORS.blue2
      })
    }

    const cardWidth = (contentWidth - 10) / 3
    const cardHeight = 30
    const cardGap = 5

    summaryCards.forEach((card, index) => {
      const row = Math.floor(index / 3)
      const col = index % 3
      const x = margin + (col * (cardWidth + cardGap))
      const y = yPosition + (row * (cardHeight + cardGap))

      doc.setFillColor(card.color)
      roundedRect(x, y, cardWidth, cardHeight, 2)

      // Icon
      const icon = getIconForMetric(card.title)
      if (icon) {
        const iconSize = 6
        try {
          const iconFormat = icon.endsWith('.png') ? 'PNG' : 'SVG'
          doc.addImage(icon, iconFormat, x + 4, y + 4, iconSize, iconSize)
        } catch (err) {
          console.log('Could not add icon:', err)
        }
      }

      doc.setTextColor(COLORS.white)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      const titleLines = card.title.split('\n')
      titleLines.forEach((line, lineIndex) => {
        doc.text(line, x + cardWidth / 2, y + 7 + (lineIndex * 4), { align: 'center' })
      })

      doc.setFontSize(16)
      doc.setFont('helvetica', 'bold')
      doc.text(card.calloutValue.toString(), x + cardWidth / 2, y + cardHeight - 7, { align: 'center' })
    })

    const maxRows = Math.ceil(summaryCards.length / 3)
    yPosition += (maxRows * (cardHeight + cardGap)) + 10
  }

  // Draw footer with disclaimer
  const drawFooter = (pageNum, totalPages) => {
    // Disclaimer section (only on last page)
    if (pageNum === totalPages) {
      const disclaimerY = pageHeight - 35

      // Disclaimer box
      doc.setFillColor(COLORS.veryLightGray)
      roundedRect(margin, disclaimerY, contentWidth, 20, 2)

      doc.setFontSize(7)
      doc.setTextColor(COLORS.dark)
      doc.setFont('helvetica', 'bold')
      doc.text('Disclaimer:', margin + 3, disclaimerY + 5)

      doc.setFont('helvetica', 'normal')
      const disclaimerText = 'We will share an annual carbon savings certificate that is validated and accurate. If you have not received it yet, please email us at info@neutralfuels.com'
      const lines = doc.splitTextToSize(disclaimerText, contentWidth - 6)
      doc.text(lines, margin + 3, disclaimerY + 10)
    }

    // Footer with timestamp and page numbers
    doc.setFontSize(8)
    doc.setTextColor(COLORS.lightGray)
    doc.setFont('helvetica', 'normal')

    const footerY = pageHeight - 10
    doc.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, footerY)
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin, footerY, { align: 'right' })
  }

  // Generate PDF content
  drawHeader()
  drawReportInfo()

  // Determine which customers to include
  const customersToInclude = includeAllCustomers
    ? customers
    : [customers[currentCustomerIndex]]

  const allCards = customersToInclude.map((_, index) => data.cardData[includeAllCustomers ? index : currentCustomerIndex])
  const isGroupCustomer = data.isGroupCustomer || false

  // Draw customer sections
  customersToInclude.forEach((customer, index) => {
    const customerCards = allCards[index]
    if (customerCards && customerCards.length > 0) {
      drawCustomerSection(customer, customerCards, isGroupCustomer)
    }
  })

  // Draw grand totals if multiple customers
  if (includeAllCustomers && customersToInclude.length > 1) {
    drawGrandTotals(allCards, isGroupCustomer)
  }

  // Add footer to all pages
  const totalPages = doc.internal.pages.length - 1
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    drawFooter(i, totalPages)
  }

  return doc
}
